import React, { useEffect } from "react";
import Select from "react-select";
import { getIn } from "formik";
import PropTypes from "prop-types";
import { normalizeRequirementOptions, requirementOptionsWithSavedAllocations, resourceRequirementProblem, withResourceAllocationPayload } from "../../helpers/resourceAllocations";

export const hasRequirementResourceOptions = (response) => Array.isArray(response?.resource_requirements) || Array.isArray(response?.requirements);
export const hasResourceConfigurationProblem = (response) => normalizeRequirementOptions(response).some(resourceRequirementProblem);
export const requiresAppointmentResource = (response) =>
  (Array.isArray(response) && response.length > 0)
  || (hasRequirementResourceOptions(response)
    && (response.resource_requirements || response.requirements).length > 0);

const AppointmentResourceFields = ({ formik, response, fieldName = "", onSelectionChange, t = (value) => value }) => {
  const selectedValues = fieldName
    ? fieldName.split(".").reduce((value, key) => value?.[key], formik.values) || {}
    : formik.values;
  const groups = requirementOptionsWithSavedAllocations(response, selectedValues.resource_allocations);
  const current = Object.fromEntries((selectedValues.resource_allocations || []).map((row) => [row.requirement_id, row.resource_item_id]));
  const availableGroups = normalizeRequirementOptions(response);
  const automaticAllocations = availableGroups
    .filter((group) => group.options.length === 1 && !current[group.requirement_id])
    .map((group) => ({ requirement_id: group.requirement_id, resource_item_id: group.options[0].value }));
  useEffect(() => {
    if (!automaticAllocations.length) return;
    const allocations = [
      ...(selectedValues.resource_allocations || []),
      ...automaticAllocations,
    ];
    const next = withResourceAllocationPayload(selectedValues, allocations);
    const prefix = fieldName ? `${fieldName}.` : "";
    formik.setFieldValue(`${prefix}resource_allocations`, next.resource_allocations, true);
    formik.setFieldValue(`${prefix}resource_item_id`, next.resource_item_id, false);
    onSelectionChange?.({ ...formik.values, ...next });
  }, [fieldName, formik.setFieldValue, selectedValues.resource_allocations, automaticAllocations.map((row) => `${row.requirement_id}:${row.resource_item_id}`).join(",")]);
  const selectItem = (requirementId, option) => {
    const choices = { ...current, [requirementId]: option?.value || "" };
    const allocations = groups.map((group) => ({ requirement_id: group.requirement_id, resource_item_id: Number(choices[group.requirement_id]) }))
      .filter((row) => row.resource_item_id > 0);
    const next = withResourceAllocationPayload(selectedValues, allocations);
    const prefix = fieldName ? `${fieldName}.` : "";
    formik.setFieldValue(`${prefix}resource_allocations`, next.resource_allocations || [], true);
    formik.setFieldValue(`${prefix}resource_item_id`, next.resource_item_id || "", false);
    onSelectionChange?.({ ...formik.values, ...next });
  };
  const prefix = fieldName ? `${fieldName}.` : "";
  const validationError = ["resource_item_id", "resource_allocations"]
    .map((field) => (formik.submitCount > 0 || getIn(formik.touched, `${prefix}${field}`))
      ? getIn(formik.errors, `${prefix}${field}`) : null)
    .find((error) => typeof error === "string" && error);
  if (!groups.length) return null;
  return <div data-testid="appointment-resource-allocations">{groups.map((group) => <div className="mb-3" key={group.requirement_id}>
    <label className="form-label">{group.label}</label>
    <Select options={group.options} isClearable placeholder={t("Choose Resource")} aria-label={group.label} value={group.options.find((option) => option.value === Number(current[group.requirement_id])) || null} onChange={(option) => selectItem(group.requirement_id, option)} />
    {group.options.length === 0 && <p className="text-danger mt-2" role="alert">{t(resourceRequirementProblem(group) || `No ${group.label} is available at this time. Choose another time.`)}</p>}
  </div>)}{validationError && <p className="text-danger mt-2" role="alert">{validationError}</p>}</div>;
};

export default AppointmentResourceFields;

AppointmentResourceFields.propTypes = {
  formik: PropTypes.shape({ submitCount: PropTypes.number, touched: PropTypes.object, errors: PropTypes.oneOfType([PropTypes.object, PropTypes.string]), values: PropTypes.object.isRequired, setFieldValue: PropTypes.func.isRequired }).isRequired,
  response: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  fieldName: PropTypes.string,
  onSelectionChange: PropTypes.func,
  t: PropTypes.func,
};
